import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Container, InputAdornment, Link, Paper, Skeleton,
  Stack, TextField, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { BsBoxArrowUpRight, BsSearch } from 'react-icons/bs';

import axiosInstance from '../../../../axiosInstance';
import { themeExpert } from '../../../../theme/expert';

const euros = (v) => (v === null || v === undefined ? '—'
  : `${Number(v).toLocaleString('fr-BE', { minimumFractionDigits: 2,
                                           maximumFractionDigits: 2 })} €`);

const date = (v) => (v ? new Date(v).toLocaleDateString('fr-BE') : null);

const STATUTS = {
  'Préparation': 'warning',
  'Contradictoire': 'info',
  'Clôture': 'success',
  'Annuler': 'default',
};

/** Un texte, ses passages surlignés. */
function Surligne({ texte, surlignes = [] }) {
  const morceaux = [];
  let curseur = 0;
  surlignes.forEach(([a, b], i) => {
    if (a > curseur) morceaux.push(texte.slice(curseur, a));
    morceaux.push(
      <Box component="mark" key={i}
           sx={{ bgcolor: 'warning.light', color: 'inherit', px: 0.25,
                 borderRadius: 0.5 }}>
        {texte.slice(a, b)}
      </Box>,
    );
    curseur = b;
  });
  morceaux.push(texte.slice(curseur));
  return <>{morceaux}</>;
}

/** Ce qu'on a trouvé dans un dossier : « 2 postes de coût, 1 pièce ». */
const resume = (trouve) => trouve
  .map(({ libelle, nombre }) => `${nombre} ${libelle.toLowerCase()}${
    nombre > 1 ? 's' : ''}`.replace('poste de coûts', 'postes de coût'))
  .join(', ');

function Entree({ entree, dossierId }) {
  const { extrait } = entree;
  return (
    <Box sx={{ py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" spacing={1} alignItems="baseline"
             flexWrap="wrap" useFlexGap>
        <Chip size="small" variant="outlined" label={entree.libelle} />
        <Link href={`/edit-expertise/${dossierId}?onglet=${entree.onglet}`}
              target="_blank" rel="noopener" underline="hover"
              sx={{ fontWeight: 600, color: 'text.primary' }}>
          <Surligne texte={entree.titre || 'Sans titre'}
                    surlignes={entree.titre_surligne} />
        </Link>
        {entree.source === 'poste' && (
          <Typography variant="body2" color="text.secondary">
            réclamé {euros(entree.reclame)} → accordé{' '}
            <Box component="strong" sx={{ color: 'text.primary' }}>
              {euros(entree.accorde)}
            </Box>
          </Typography>
        )}
      </Stack>
      {extrait.texte && (
        <Typography variant="body2" color="text.secondary"
                    sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
          {extrait.coupe_debut && '… '}
          <Surligne texte={extrait.texte} surlignes={extrait.surlignes} />
          {extrait.coupe_fin && ' …'}
        </Typography>
      )}
    </Box>
  );
}

function Dossier({ resultat }) {
  const { dossier } = resultat;
  const details = [
    dossier.compagnie,
    dossier.date_sinistre && `sinistre du ${date(dossier.date_sinistre)}`,
    dossier.adresse,
  ].filter(Boolean);
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}
             justifyContent="space-between" alignItems={{ sm: 'flex-start' }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center"
                 flexWrap="wrap" useFlexGap>
            <Typography variant="h3" component="h2">
              {dossier.reference || `Dossier ${dossier.id}`}
              {dossier.nom && ` — ${dossier.nom}`}
            </Typography>
            <Chip size="small" label={dossier.statut}
                  color={STATUTS[dossier.statut] || 'default'} />
          </Stack>
          {details.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {details.join(' · ')}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Trouvé dans : {resume(resultat.trouve)}
          </Typography>
        </Box>
        <Button size="small" variant="outlined" endIcon={<BsBoxArrowUpRight />}
                href={`/edit-expertise/${dossier.id}`} target="_blank"
                rel="noopener" sx={{ flexShrink: 0 }}>
          Ouvrir le dossier
        </Button>
      </Stack>

      <Box sx={{ mt: 1.5 }}>
        {resultat.entrees.map((e, i) => (
          <Entree key={i} entree={e} dossierId={dossier.id} />
        ))}
        {resultat.autres > 0 && (
          <Typography variant="caption" color="text.secondary"
                      component="div" sx={{ pt: 1 }}>
            et {resultat.autres} autre{resultat.autres > 1 ? 's' : ''} passage
            {resultat.autres > 1 ? 's' : ''} dans ce dossier.
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

/**
 * Retrouver les dossiers de l'organisation où l'on a déjà vu un cas proche :
 * un type de dommage, une entreprise, un mot d'un devis.
 *
 * S'ouvre dans sa propre fenêtre, à côté du dossier en cours.
 */
function Recherche() {
  const [params, setParams] = useSearchParams();
  const requete = params.get('q') || '';
  const sources = (params.get('sources') || '').split(',').filter(Boolean);

  const [saisie, setSaisie] = useState(requete);
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');

  useEffect(() => { setSaisie(requete); }, [requete]);

  useEffect(() => {
    document.title = requete ? `Recherche : ${requete}` : 'Recherche';
    if (requete.trim().length < 2) { setDonnees(null); return; }
    let actif = true;
    setChargement(true);
    setErreur('');
    axiosInstance.get('/recherche/', {
      params: { q: requete, sources: sources.join(',') || undefined },
      silencieux: true,
    })
      .then(({ data }) => { if (actif) setDonnees(data); })
      .catch(() => { if (actif) setErreur("La recherche n'a pas abouti."); })
      .finally(() => { if (actif) setChargement(false); });
    return () => { actif = false; };
    // `sources` est dérivé de params : la chaîne suffit comme dépendance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requete, params.get('sources')]);

  const lancer = (e) => {
    e.preventDefault();
    const suivants = new URLSearchParams(params);
    suivants.set('q', saisie.trim());
    setParams(suivants);
  };

  const basculer = (valeur) => {
    const choisies = sources.includes(valeur)
      ? sources.filter((s) => s !== valeur) : [...sources, valeur];
    const suivants = new URLSearchParams(params);
    if (choisies.length) suivants.set('sources', choisies.join(','));
    else suivants.delete('sources');
    setParams(suivants);
  };

  const resultats = donnees?.resultats || [];

  return (
    <ThemeProvider theme={themeExpert}>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Typography variant="h1" sx={{ mb: 0.5 }}>Cas similaires</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Dans tous les dossiers de votre organisation : postes de coût et
          décisions, rapport, intervenants, texte des pièces.
        </Typography>

        <Box component="form" onSubmit={lancer}>
          <TextField
            fullWidth autoFocus value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            placeholder="Porte de garage, nom d'une entreprise, n° de devis…"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><BsSearch /></InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button type="submit" variant="contained"
                          disabled={saisie.trim().length < 2}>
                    Rechercher
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {donnees?.sources && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap
                 sx={{ mt: 1.5 }}>
            {donnees.sources.map(({ valeur, libelle }) => (
              <Chip key={valeur} label={libelle} size="small"
                    clickable onClick={() => basculer(valeur)}
                    color={sources.includes(valeur) ? 'primary' : 'default'}
                    variant={sources.includes(valeur) ? 'filled' : 'outlined'} />
            ))}
          </Stack>
        )}

        <Stack spacing={2} sx={{ mt: 3 }}>
          {erreur && <Alert severity="error">{erreur}</Alert>}
          {chargement && <Skeleton variant="rounded" height={160} />}
          {!chargement && donnees && resultats.length === 0 && (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Aucun dossier ne contient « {donnees.requete} ».
            </Typography>
          )}
          {!chargement && resultats.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {resultats.length} dossier{resultats.length > 1 ? 's' : ''}
              {resultats.length >= 30 && ' (les plus proches)'}
            </Typography>
          )}
          {!chargement && resultats.map((r) => (
            <Dossier key={r.dossier.id} resultat={r} />
          ))}
        </Stack>
      </Container>
    </ThemeProvider>
  );
}

export default Recherche;
